/**
 * Sellable Type Select Component
 *
 * Building block for selecting sellable item type (plan, group, product, package).
 * Used by purchase triggers (any, some).
 *
 * Note: The sellable type is stored in `reminderType`, not a separate field.
 * When the trigger is initially 'some' or 'any', we show this dropdown to let
 * the user select the actual sellable type.
 */

import { useFormikContext } from 'formik';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { TextField } from 'src/components/TextField';
import { SellableType } from 'src/shared/Forms/Selects/SellableItemsMultiSelect';

import { AutomationItem } from '../types';

const SELLABLE_TYPE_OPTIONS = [
  { value: 'all', label: 'Anything' },
  { value: 'workout_plan', label: 'Plan(s)' },
  { value: 'group', label: 'Group(s)' },
  { value: 'product', label: 'Product(s)' },
  { value: 'package', label: 'Package(s)' },
  { value: 'client', label: 'Client training' },
] as const;

const VALID_SELLABLE_TYPE_VALUES = SELLABLE_TYPE_OPTIONS.map((opt) => opt.value);

interface SellableTypeSelectProps {
  index: number;
  onChange?: (type: string) => void;
}

export const SellableTypeSelect: FC<SellableTypeSelectProps> = ({
  index,
  onChange,
}) => {
  const { t } = useTranslation();
  const { setFieldValue, values } = useFormikContext<{
    automations: AutomationItem[];
  }>();

  const automation = values.automations[index];
  const currentValue = automation?.reminderType || '';

  // If the current reminderType is not a valid sellable type (e.g., 'some', 'any'),
  // show empty so the placeholder displays
  const displayValue = VALID_SELLABLE_TYPE_VALUES.includes(
    currentValue as SellableType | 'all'
  )
    ? currentValue
    : '';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newType = e.target.value;
    setFieldValue(`automations.${index}.reminderType`, newType);
    // Reset selected items when type changes (v2: "any" keeps reminderTypeId empty)
    setFieldValue(`automations.${index}.reminderTypeId`, null);
    if (newType !== 'group') {
      setFieldValue(`automations.${index}.lifeTime`, false);
    }
    onChange?.(newType);
  };

  const clientLabel = `${t('trainer.client.one')} Training`;

  const options = [
    { value: '', label: 'Select type...' },
    ...SELLABLE_TYPE_OPTIONS.map((opt) => ({
      value: opt.value,
      label: opt.value === 'client' ? clientLabel : opt.label,
    })),
  ];

  return (
    <Box sx={{ maxWidth: 300 }}>
      <TextField
        value={displayValue}
        onChange={handleChange}
        options={options}
        size="small"
      />
    </Box>
  );
};
